import React, { useState, useEffect } from "react";
import { Link, NavLink, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, Globe } from "lucide-react";
import EscudoNacional from "./common/EscudoNacional";

const Header = () => {
  const { t } = useTranslation();
  const { lang } = useParams();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const currentLang = lang === "en" ? "en" : "pe";
  const otherLang = currentLang === "pe" ? "en" : "pe";

  const buildPath = (targetLang) => {
    const rest = location.pathname.replace(/^\/(pe|en)/, "");
    return `/${targetLang}${rest}`;
  };

  const navLinks = [
    { to: `/${currentLang}`, label: t("nav.home"), end: true, id: "home" },
    { to: `/${currentLang}/privacy`, label: t("nav.privacy"), id: "privacy" },
    { to: `/${currentLang}/cookies`, label: t("nav.cookies"), id: "cookies" },
    { to: `/${currentLang}/terms`, label: t("nav.terms"), id: "terms" },
  ];

  return (
    <header
      data-testid="site-header"
      style={{ background: "var(--pum-navy)", color: "#fff", position: "sticky", top: 0, zIndex: 40 }}
    >
      <div className="pum-flag-ribbon pum-flag-ribbon--thin" aria-hidden="true" />
      <div
        className="pum-container"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: "76px",
        }}
      >
        <Link
          to={`/${currentLang}`}
          data-testid="header-logo-link"
          style={{ display: "flex", alignItems: "center", gap: "0.85rem" }}
        >
          <EscudoNacional size={40} />
          <span style={{ display: "flex", flexDirection: "column", lineHeight: 1.05 }}>
            <span
              style={{
                fontFamily: "var(--font-serif)",
                fontWeight: 900,
                fontSize: "1.35rem",
                color: "#fff",
                letterSpacing: "0.02em",
              }}
            >
              {t("brand.name")}
            </span>
            <span
              style={{
                fontSize: "0.68rem",
                textTransform: "uppercase",
                letterSpacing: "0.24em",
                color: "#8FA5C7",
              }}
            >
              Perú · 2026
            </span>
          </span>
        </Link>

        {/* Desktop nav */}
        <nav
          data-testid="desktop-nav"
          className="pum-desktop-nav"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "2rem",
          }}
        >
          {navLinks.map((l) => (
            <NavLink
              key={l.id}
              to={l.to}
              end={l.end}
              data-testid={`nav-desktop-${l.id}`}
              style={({ isActive }) => ({
                fontSize: "0.9rem",
                fontWeight: 500,
                color: isActive ? "#fff" : "#C6D2E5",
                borderBottom: isActive ? "2px solid var(--pum-emerald)" : "2px solid transparent",
                paddingBottom: "4px",
              })}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        {/* Right controls */}
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          <Link
            to={buildPath(otherLang)}
            data-testid="lang-switch-desktop"
            replace={false}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.4rem",
              padding: "0.55rem 0.85rem",
              border: "1px solid rgba(255,255,255,0.25)",
              borderRadius: "4px",
              color: "#fff",
              fontSize: "0.82rem",
              fontWeight: 600,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            <Globe size={14} />
            {otherLang === "en" ? "EN" : "ES"}
          </Link>

          <button
            type="button"
            className="pum-hamburger-btn"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label={open ? t("nav.close") : t("nav.menu")}
            style={{
              background: "transparent",
              border: "1px solid rgba(255,255,255,0.25)",
              padding: "0.5rem",
              borderRadius: "4px",
              color: "#fff",
              cursor: "pointer",
            }}
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-nav-panel"
          style={{
            background: "var(--pum-navy-2)",
            borderTop: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <div className="pum-container" style={{ padding: "1rem 1.25rem 1.5rem" }}>
            <nav style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
              {navLinks.map((l) => (
                <NavLink
                  key={l.id}
                  to={l.to}
                  end={l.end}
                  data-testid={`nav-mobile-${l.id}`}
                  style={({ isActive }) => ({
                    padding: "0.85rem 0.25rem",
                    fontSize: "0.98rem",
                    fontWeight: 500,
                    color: isActive ? "var(--pum-emerald)" : "#E4EBF7",
                    borderBottom: "1px solid rgba(255,255,255,0.06)",
                  })}
                >
                  {l.label}
                </NavLink>
              ))}
            </nav>
          </div>
        </div>
      )}

      <style>{`
        .pum-hamburger-btn { display: none; }
        @media (max-width: 900px) {
          [data-testid="desktop-nav"] { display: none !important; }
          .pum-hamburger-btn { display: inline-flex !important; }
        }
      `}</style>
    </header>
  );
};

export default Header;
